import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import {
  Loader2,
  RefreshCw,
  Play,
  Image as ImageIcon,
  ExternalLink,
  Bookmark,
  Eye,
  BarChart,
  BarChart3,
  ShoppingCart,
  TrendingUp,
  Layers,
  Settings,
  Music,
  Heart,
  MessageCircle,
  Share2,
  UserPlus,
} from 'lucide-react';

import { isDemoBrand, demoHash } from '../../../utils/demoBrand';

const API_BASE = 'https://api.glowytics.com';

// Static TikTok creative-performance rows for demo brands. picsum stands in for
// video covers/profile images; video/spark ids left null so cards show the cover.
function buildDemoTikTokRows() {
  const names = [
    'Yeni Sezon Lansmanı', 'Ürün Tanıtım - Sneaker', 'UGC - Müşteri Yorumu', 'İndirim Kampanyası',
    'Marka Hikayesi', 'Trend Challenge', 'Katalog - Çanta', 'Reels Remix', 'Influencer Collab',
    'Before/After', 'Stok Eritme', 'Sezon Finali',
  ];
  const ctas = ['SHOP_NOW', 'LEARN_MORE', 'ORDER_NOW', 'SIGN_UP'];
  const texts = [
    'Sezonun trendleri TikTok’ta! %30’a varan indirim.',
    'Şimdi keşfet, hızlı kargo ile kapında.',
    'Gerçek kullanıcı deneyimi 👇',
    'Sınırlı stok — kaçırma!',
  ];
  return names.map((n, i) => {
    const h = demoHash('tt' + n);
    const impressions = 40000 + (h % 400000);
    const clicks = Math.round(impressions * (0.006 + (h % 20) / 1000));
    const spend = Math.round(impressions * (0.02 + (h % 40) / 1000));
    const conversions = Math.round(clicks * (0.02 + (h % 25) / 1000));
    const plays = Math.round(impressions * (0.6 + (h % 30) / 100));
    return {
      ad_id: `demo-tt-${i}`,
      ad_name: n,
      campaign_id: `demo-camp-${i % 4}`,
      adgroup_id: `demo-ag-${i % 6}`,
      creative_id: `demo-cr-${i}`,
      ad_format: 'SINGLE_VIDEO',
      creative_material_mode: 'CUSTOM',
      ad_text: texts[h % texts.length],
      display_name: 'Glowytics Demo Store',
      call_to_action: ctas[h % ctas.length],
      landing_url: 'https://demo.glowytics.com',
      profile_image: `https://picsum.photos/seed/ttprofile${i}/80/80`,
      video_id: null,
      tiktok_item_id: null,
      image_ids: null,
      video_cover_url: `https://picsum.photos/seed/ttcover${i}/300/533`,
      video_preview_url: null,
      video_duration: 15 + (h % 30),
      video_width: 1080,
      video_height: 1920,
      status: 'ENABLE',
      spend, impressions, clicks, conversions,
      conversions_value: Math.round(conversions * (400 + (h % 600))),
      video_play_actions: plays,
      video_views_p100: Math.round(plays * (0.15 + (h % 25) / 100)),
      ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    };
  });
}

const DATE_RANGE_OPTIONS = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last14', label: 'Last 14 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

function fmtTRY(n) {
  return '₺' + Math.round(Number(n || 0)).toLocaleString('tr-TR');
}
function fmtNum(n) {
  return Number(n || 0).toLocaleString('tr-TR');
}
function fmtPct(n) {
  return ((Number(n || 0))).toFixed(2) + '%'; // ctr already in pct from TikTok
}
function fmtLarge(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(2).replace(/\.?0+$/, '') + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return Math.round(v).toLocaleString('tr-TR');
}
function safe(n) { return Number(n || 0); }

export default function TikTokCreativePerformance() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('last30');
  const [formatFilter, setFormatFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });
  const [selectedAdId, setSelectedAdId] = useState(null);
  const [showSyncMenu, setShowSyncMenu] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        setError('No brand selected');
        setRows([]);
        return;
      }
      const resp = await axios.get(`${API_BASE}/api/tiktok-ads/ad-performance-with-creatives`, {
        params: { dateRange },
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId },
      });
      const apiRows = Array.isArray(resp?.data?.rows) ? resp.data.rows : [];
      setRows((apiRows.length === 0 && isDemoBrand(brandId)) ? buildDemoTikTokRows() : apiRows);
    } catch (e) {
      if (isDemoBrand(localStorage.getItem('currentBrandId'))) {
        setRows(buildDemoTikTokRows());
        setError('');
      } else {
        setError(e?.response?.data?.error || e.message || 'Failed to load');
        setRows([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const triggerSync = async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    setSyncing(true);
    setSyncStatus(`Syncing creative media…`);
    try {
      const resp = await axios.post(`${API_BASE}/api/tiktok-ads/sync/creative-media`, {}, {
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId },
      });
      setSyncStatus(JSON.stringify(resp.data));
      setShowSyncMenu(false);
      await load();
    } catch (e) {
      setSyncStatus(`Error: ${e?.response?.data?.error || e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  // Aggregate by ad_id (TikTok ad = one creative)
  const adAgg = useMemo(() => {
    const agg = {};
    for (const r of rows) {
      const key = r.ad_id;
      if (!agg[key]) {
        agg[key] = {
          ad_id: r.ad_id,
          ad_name: r.ad_name || null,
          campaign_id: r.campaign_id,
          adgroup_id: r.adgroup_id,
          creative_id: r.creative_id,
          ad_format: r.ad_format,
          creative_material_mode: r.creative_material_mode,
          ad_text: r.ad_text,
          display_name: r.display_name,
          call_to_action: r.call_to_action,
          landing_url: r.landing_url || r.landing_page_url,
          profile_image: r.profile_image,
          video_id: r.video_id,
          tiktok_item_id: r.tiktok_item_id,
          image_ids: r.image_ids,
          video_cover_url: r.video_cover_url,
          video_preview_url: r.video_preview_url,
          video_duration: r.video_duration,
          video_width: r.video_width,
          video_height: r.video_height,
          status: r.status,
          spend: 0, impressions: 0, clicks: 0, conversions: 0, conversions_value: 0,
          video_play_actions: 0, video_views_p100: 0,
          ctr_rows: 0, ctr_sum: 0,
        };
      }
      const a = agg[key];
      a.spend += safe(r.spend);
      a.impressions += safe(r.impressions);
      a.clicks += safe(r.clicks);
      a.conversions += safe(r.conversions);
      a.conversions_value += safe(r.conversions_value);
      a.video_play_actions += safe(r.video_play_actions);
      a.video_views_p100 += safe(r.video_views_p100);
      a.ctr_rows += 1;
      a.ctr_sum += safe(r.ctr);
    }
    return Object.values(agg).map(a => ({
      ...a,
      ctr: a.impressions > 0 ? (a.clicks / a.impressions) * 100 : 0,
      cpc: a.clicks > 0 ? a.spend / a.clicks : 0,
      cpm: a.impressions > 0 ? (a.spend / a.impressions) * 1000 : 0,
      cpa: a.conversions > 0 ? a.spend / a.conversions : 0,
      roas: a.spend > 0 ? a.conversions_value / a.spend : 0,
      vtr: a.video_play_actions > 0 ? (a.video_views_p100 / a.video_play_actions) * 100 : 0,
    }));
  }, [rows]);

  const formats = useMemo(() => {
    const s = new Set();
    adAgg.forEach(p => { if (p.ad_format) s.add(p.ad_format); });
    return ['all', ...Array.from(s)];
  }, [adAgg]);

  const filtered = useMemo(() => {
    const list = formatFilter === 'all'
      ? adAgg
      : adAgg.filter(p => p.ad_format === formatFilter);
    const k = sort.key;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => ((a[k] ?? 0) - (b[k] ?? 0)) * dir);
  }, [adAgg, formatFilter, sort]);

  const selectedAd = useMemo(
    () => filtered.find(p => p.ad_id === selectedAdId) || filtered[0] || null,
    [filtered, selectedAdId]
  );

  useEffect(() => {
    if (!selectedAdId && filtered.length > 0) setSelectedAdId(filtered[0].ad_id);
  }, [filtered, selectedAdId]);

  const totals = useMemo(() => {
    const t = filtered.reduce((acc, p) => {
      acc.spend += p.spend;
      acc.impressions += p.impressions;
      acc.clicks += p.clicks;
      acc.conversions += p.conversions;
      acc.conversions_value += p.conversions_value;
      return acc;
    }, { spend: 0, impressions: 0, clicks: 0, conversions: 0, conversions_value: 0 });
    return {
      ...t,
      roas: t.spend > 0 ? t.conversions_value / t.spend : 0,
    };
  }, [filtered]);

  const SortableTh = ({ k, label }) => (
    <th
      className="py-2 px-3 text-right cursor-pointer select-none"
      onClick={() => setSort(prev => ({ key: k, dir: prev.key === k && prev.dir === 'desc' ? 'asc' : 'desc' }))}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && <span>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );

  return (
    <div className="min-h-screen p-4 space-y-6 max-w-screen-2xl mx-auto w-full overflow-x-hidden bg-[#f6f8ff]/70">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-5 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-black/5 border border-black/10 shadow-sm rounded-xl">
            <img
              src="/tik-tokiconss.png"
              alt="TikTok Logo"
              className="w-8 h-8 object-contain"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Creative Performance</h1>
            <p className="text-sm text-gray-500 mt-1">Analyze your TikTok ad creatives</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {formats.length > 1 && (
            <select
              value={formatFilter}
              onChange={(e) => setFormatFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-pink-100 outline-none transition-all hover:border-gray-300 min-w-[140px]"
            >
              {formats.map(t => <option key={t} value={t}>{t === 'all' ? 'All formats' : t}</option>)}
            </select>
          )}
          <select
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-pink-100 outline-none transition-all hover:border-gray-300"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            {DATE_RANGE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <div className="relative">
            <button
              onClick={() => setShowSyncMenu(s => !s)}
              className="px-2.5 py-2 bg-white border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50 transition-all"
              title="Sync options"
            >
              <Settings className="w-4 h-4" />
            </button>
            {showSyncMenu && (
              <div className="absolute right-0 mt-2 w-60 bg-white border rounded-xl shadow-xl z-30 overflow-hidden">
                <button
                  className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 disabled:opacity-50"
                  disabled={syncing}
                  onClick={triggerSync}
                >
                  {syncing ? 'Syncing…' : 'Sync creative media (video + image URLs)'}
                </button>
                {syncStatus && (
                  <div className="px-3 py-2 text-[10px] text-gray-500 border-t bg-gray-50 break-all font-mono max-h-32 overflow-auto">{syncStatus}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Performance Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Impressions', value: fmtLarge(totals.impressions), icon: Eye, color: 'blue' },
          { label: 'Revenue', value: fmtTRY(totals.conversions_value), icon: BarChart, color: 'emerald' },
          { label: 'Spend', value: fmtTRY(totals.spend), icon: BarChart3, color: 'indigo' },
          { label: 'Conversions', value: fmtLarge(totals.conversions), icon: ShoppingCart, color: 'purple' },
          { label: 'ROAS', value: totals.roas.toFixed(2) + 'x', icon: TrendingUp, color: 'rose' },
        ].map((metric, idx) => (
          <div key={idx} className="p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow bg-white">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl bg-${metric.color}-50`}>
                  <metric.icon className={`w-5 h-5 text-${metric.color}-600`} />
                </div>
                <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
                  {DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label || ''}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">{metric.label}</p>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>
      )}

      {/* Main Layout: Ad list (left) + Preview (right) */}
      <div className="flex flex-col lg:flex-row gap-6 relative">
        {/* Left: ad list */}
        <div className="w-full lg:w-7/12 space-y-6">
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
            <div className="p-4 border-b bg-white text-gray-800 font-medium flex justify-between items-center">
              <span className="text-sm font-medium">Ads</span>
              <span className="text-xs text-gray-500">Total: {filtered.length}</span>
            </div>
            <div className="overflow-x-auto">
              <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-left text-gray-500 font-semibold">
                      <th className="py-2 px-3">Ad</th>
                      <SortableTh k="spend" label="Spend" />
                      <SortableTh k="conversions_value" label="Revenue" />
                      <SortableTh k="conversions" label="Conv." />
                      <SortableTh k="clicks" label="Clicks" />
                      <SortableTh k="impressions" label="Impr." />
                      <SortableTh k="cpm" label="CPM" />
                      <SortableTh k="roas" label="ROAS" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr><td colSpan={8} className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={8} className="py-10 text-center text-sm text-gray-500">No data. Try a wider date range or run sync (gear icon → Sync).</td></tr>
                    ) : (
                      filtered.map((p, idx) => {
                        const isSelected = selectedAd?.ad_id === p.ad_id;
                        return (
                          <tr
                            key={p.ad_id || idx}
                            className={`cursor-pointer transition-colors ${isSelected ? 'bg-pink-50 border-l-2 border-pink-500' : 'hover:bg-gray-50'}`}
                            onClick={() => setSelectedAdId(p.ad_id)}
                          >
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="relative w-9 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                                  {p.video_cover_url ? (
                                    <img src={p.video_cover_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                                  ) : p.profile_image ? (
                                    <img src={p.profile_image} alt="" className="w-full h-full object-cover" loading="lazy" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                                      <ImageIcon className="w-3.5 h-3.5" />
                                    </div>
                                  )}
                                  {(p.video_id || p.tiktok_item_id) && (
                                    <div className="absolute bottom-0.5 right-0.5 bg-black/60 rounded-full p-0.5">
                                      <Play className="w-2 h-2 text-white" fill="white" />
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="text-xs font-medium text-gray-800 truncate max-w-[220px]">{p.ad_name || p.ad_id}</div>
                                  <div className="text-[10px] text-gray-500 truncate max-w-[220px]">
                                    {p.ad_format || '—'}{p.tiktok_item_id ? ' · Spark Ad' : (p.creative_material_mode ? ' · ' + p.creative_material_mode : '')}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtTRY(p.spend)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtTRY(p.conversions_value)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtNum(p.conversions)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtNum(p.clicks)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{fmtNum(p.impressions)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{p.cpm > 0 ? fmtTRY(p.cpm) : '—'}</td>
                            <td className="py-2 px-3 text-right text-gray-700 tabular-nums">{p.roas > 0 ? p.roas.toFixed(2) + 'x' : '—'}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Preview */}
        <div className="w-full lg:w-5/12 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden sticky top-4">
            {selectedAd ? (
              <>
                <div className="p-4">
                  <TikTokAdPreview ad={selectedAd} />
                </div>
                <div className="border-t border-gray-100 p-4 space-y-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">{selectedAd.ad_format || 'TikTok ad'}</div>
                    <div className="text-sm font-semibold text-gray-900 mt-0.5">{selectedAd.ad_name || selectedAd.ad_id}</div>
                    {selectedAd.ad_text && <p className="text-xs text-gray-600 mt-1 line-clamp-3">{selectedAd.ad_text}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-[9px] uppercase text-gray-500 font-medium">Campaign</div>
                      <div className="text-gray-800 truncate mt-0.5">{selectedAd.campaign_id || '—'}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2">
                      <div className="text-[9px] uppercase text-gray-500 font-medium">Ad Group</div>
                      <div className="text-gray-800 truncate mt-0.5">{selectedAd.adgroup_id || '—'}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Spend', value: fmtTRY(selectedAd.spend) },
                      { label: 'Revenue', value: fmtTRY(selectedAd.conversions_value) },
                      { label: 'Impressions', value: fmtNum(selectedAd.impressions) },
                      { label: 'Clicks', value: fmtNum(selectedAd.clicks) },
                      { label: 'CTR', value: selectedAd.ctr ? selectedAd.ctr.toFixed(2) + '%' : '—' },
                      { label: 'CPC', value: selectedAd.cpc > 0 ? fmtTRY(selectedAd.cpc) : '—' },
                      { label: 'CPM', value: selectedAd.cpm > 0 ? fmtTRY(selectedAd.cpm) : '—' },
                      { label: 'CPA', value: selectedAd.cpa > 0 ? fmtTRY(selectedAd.cpa) : '—' },
                      { label: 'Conversions', value: fmtNum(selectedAd.conversions) },
                      { label: 'ROAS', value: selectedAd.roas > 0 ? selectedAd.roas.toFixed(2) + 'x' : '—',
                        highlight: selectedAd.roas >= 2 ? 'emerald' : selectedAd.roas >= 1 ? 'amber' : null },
                      { label: 'Video plays', value: fmtNum(selectedAd.video_play_actions) },
                      { label: 'Completion rate', value: selectedAd.vtr > 0 ? selectedAd.vtr.toFixed(1) + '%' : '—', highlight: 'pink' },
                    ].map((m, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-2">
                        <div className="text-[9px] uppercase text-gray-500 font-medium">{m.label}</div>
                        <div className={`text-xs font-semibold mt-0.5 ${
                          m.highlight === 'pink' ? 'text-pink-600' :
                          m.highlight === 'emerald' ? 'text-emerald-600' :
                          m.highlight === 'amber' ? 'text-amber-600' : 'text-gray-900'
                        }`}>{m.value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <div className="text-[11px] text-gray-500">
                      <Layers className="w-3 h-3 inline mr-1" /> {selectedAd.creative_material_mode || 'Single creative'}
                    </div>
                    {selectedAd.landing_url && (
                      <a
                        href={selectedAd.landing_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-pink-600 hover:text-pink-700 hover:underline"
                      >
                        Landing page <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="p-10 text-center text-sm text-gray-400">Select an ad to see preview & metrics</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TikTok ad preview (9:16 phone frame, autoplay video) ──────────────────
function TikTokAdPreview({ ad }) {
  const [previewUrl, setPreviewUrl] = useState(ad.video_preview_url || null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  // Reset when switching ad
  useEffect(() => {
    setPreviewUrl(ad.video_preview_url || null);
    setPlaying(false);
    setRefreshError('');
  }, [ad.ad_id, ad.video_preview_url]);

  const refreshPreview = async () => {
    if (!ad.video_id) return;
    setRefreshing(true);
    setRefreshError('');
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const resp = await axios.get(`${API_BASE}/api/tiktok-ads/live/video-preview-refresh`, {
        params: { videoId: ad.video_id },
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId },
      });
      const url = resp?.data?.preview_url;
      if (url) {
        setPreviewUrl(url);
        setPlaying(true);
        setTimeout(() => { if (videoRef.current) videoRef.current.play().catch(() => {}); }, 50);
      } else {
        setRefreshError('No preview URL returned');
      }
    } catch (e) {
      setRefreshError(e?.response?.data?.error || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  // Check expiration: if we have URL but it's known to expire, the GET will 403/404.
  // Simplest: assume URL might be stale; user clicks play → if it errors, refresh.

  // Spark Ad (organic TikTok post) — use official TikTok iframe embed
  if (ad.tiktok_item_id) {
    return (
      <div className="relative mx-auto" style={{ maxWidth: 280 }}>
        <div className="relative bg-black rounded-[2rem] overflow-hidden border-[6px] border-black shadow-2xl" style={{ aspectRatio: '9 / 16' }}>
          <iframe
            title="TikTok pin preview"
            src={`https://www.tiktok.com/embed/v2/${ad.tiktok_item_id}?lang=tr-TR`}
            className="absolute inset-0 w-full h-full border-0"
            allow="autoplay; encrypted-media; picture-in-picture; clipboard-write"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 text-[10px]">
          <span className="text-gray-500 inline-flex items-center gap-1"><Music className="w-3 h-3" /> Spark Ad</span>
          <a
            href={`https://www.tiktok.com/embed/v2/${ad.tiktok_item_id}`}
            target="_blank"
            rel="noreferrer"
            className="text-pink-600 hover:text-pink-700 hover:underline inline-flex items-center gap-1"
          >
            Open on TikTok <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    );
  }

  // Uploaded video ad — use signed preview URL (expires) with refresh button
  return (
    <div className="relative mx-auto" style={{ maxWidth: 280 }}>
      {/* Phone frame */}
      <div className="relative bg-black rounded-[2rem] overflow-hidden border-[6px] border-black shadow-2xl" style={{ aspectRatio: '9 / 16' }}>
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-b-2xl z-30" />

        {/* Video / cover */}
        <div className="absolute inset-0">
          {ad.video_id && previewUrl && playing ? (
            <video
              ref={videoRef}
              src={previewUrl}
              poster={ad.video_cover_url || undefined}
              controls
              playsInline
              autoPlay
              className="w-full h-full object-cover"
              onError={() => { setPlaying(false); setRefreshError('Preview expired — click play to refresh'); }}
            />
          ) : ad.video_cover_url ? (
            <img src={ad.video_cover_url} alt="" className="w-full h-full object-cover" />
          ) : ad.profile_image ? (
            <img src={ad.profile_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500 text-xs">
              <ImageIcon className="w-8 h-8" />
            </div>
          )}
        </div>

        {/* Play overlay when not playing */}
        {ad.video_id && !playing && (
          <button
            onClick={() => {
              if (previewUrl) {
                setPlaying(true);
                setTimeout(() => { if (videoRef.current) videoRef.current.play().catch(() => {}); }, 50);
              } else {
                refreshPreview();
              }
            }}
            disabled={refreshing}
            className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors z-20"
          >
            <div className="bg-white/95 rounded-full p-4 shadow-lg">
              {refreshing ? <Loader2 className="w-6 h-6 animate-spin text-gray-700" /> : <Play className="w-6 h-6 text-gray-900" fill="currentColor" />}
            </div>
          </button>
        )}

        {/* Right side actions (TikTok-style) */}
        <div className="absolute right-2 bottom-20 flex flex-col gap-3 items-center z-20">
          {ad.profile_image && (
            <div className="w-9 h-9 rounded-full border-2 border-white overflow-hidden bg-gray-200">
              <img src={ad.profile_image} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <SideAction icon={Heart} label="" />
          <SideAction icon={MessageCircle} label="" />
          <SideAction icon={Bookmark} label="" />
          <SideAction icon={Share2} label="" />
        </div>

        {/* Bottom overlay: caption + CTA */}
        <div className="absolute bottom-0 left-0 right-12 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10">
          {ad.display_name && (
            <div className="text-white text-xs font-semibold drop-shadow">@{ad.display_name}</div>
          )}
          {ad.ad_text && (
            <div className="text-white text-[11px] mt-0.5 leading-tight line-clamp-3 drop-shadow">{ad.ad_text}</div>
          )}
          {ad.call_to_action && (
            <div className="mt-2">
              <span className="inline-block bg-[#fe2c55] hover:bg-[#e0264c] text-white text-[11px] font-semibold px-3 py-1 rounded-md shadow">
                {prettyCta(ad.call_to_action)}
              </span>
            </div>
          )}
          <div className="text-white/70 text-[10px] mt-1 flex items-center gap-1">
            <Music className="w-2.5 h-2.5" /> Sponsored
          </div>
        </div>
      </div>

      {refreshError && (
        <div className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5">{refreshError}</div>
      )}
      {ad.video_id && (
        <button
          onClick={refreshPreview}
          disabled={refreshing}
          className="mt-2 w-full text-[10px] text-gray-500 hover:text-gray-700 inline-flex items-center justify-center gap-1 disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh preview URL (expires ~24h)
        </button>
      )}
    </div>
  );
}

function SideAction({ icon: Icon }) {
  return (
    <div className="w-8 h-8 rounded-full bg-black/30 backdrop-blur flex items-center justify-center">
      <Icon className="w-4 h-4 text-white" />
    </div>
  );
}

function prettyCta(s) {
  if (!s) return 'Learn more';
  return String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
